(() => {
  const normalize = (value) => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase('fr');
  const track = (name, detail = {}) => {
    document.dispatchEvent(new CustomEvent(`french_bites:${name}`, { detail }));
    if (Array.isArray(window.dataLayer)) window.dataLayer.push({ event: `french_bites_${name}`, ...detail });
  };
  const parse = (root, selector, first) => {
    const node = root.querySelector(selector);
    if (!node) return [];
    try {
      return JSON.parse(node.textContent || '[]').map((row, index) => ({
        position: first + index, french: row[0], synonym: row[1], sense: row[2],
        category: row[3], headAudio: row[4], synonymAudio: row[5],
      }));
    } catch (error) {
      console.error('French synonym data could not be loaded.', error);
      return [];
    }
  };
  const initialize = (root) => {
    const data = [...parse(root, '[data-fsy-free-data]', 1), ...parse(root, '[data-fsy-member-data]', 101)];
    const member = root.dataset.member === 'true';
    const audioEnabled = root.dataset.audioEnabled === 'true';
    const audioPath = root.dataset.audioPath || '';
    const pageSize = Math.max(25, Number.parseInt(root.dataset.pageSize || '50', 10));
    const list = root.querySelector('[data-fsy-list]');
    const search = root.querySelector('[data-fsy-search]');
    const count = root.querySelector('[data-fsy-count]');
    const empty = root.querySelector('[data-fsy-empty]');
    const previous = root.querySelector('[data-fsy-previous]');
    const next = root.querySelector('[data-fsy-next]');
    const pageStatus = root.querySelector('[data-fsy-page-status]');
    const liveStatus = root.querySelector('[data-fsy-live-status]');
    if (!list || !search || !count || !previous || !next || !pageStatus) return;
    let page = 1;
    let query = '';
    let currentAudio = null;
    let currentButton = null;
    let debounceTimer = null;
    const stopAudio = () => {
      if (currentAudio) { currentAudio.pause(); currentAudio.currentTime = 0; }
      if (currentButton) { currentButton.setAttribute('aria-busy', 'false'); currentButton.removeAttribute('aria-pressed'); }
      currentAudio = null;
      currentButton = null;
    };
    const audioUrl = (reference) => /^https:\/\//i.test(reference) ? reference : `${audioPath}${encodeURIComponent(reference)}`;
    const play = async (word, reference, button, position) => {
      stopAudio();
      currentButton = button;
      button.setAttribute('aria-busy', 'true');
      button.setAttribute('aria-pressed', 'true');
      currentAudio = new Audio(audioUrl(reference));
      currentAudio.preload = 'none';
      currentAudio.addEventListener('playing', () => {
        button.setAttribute('aria-busy', 'false');
        if (liveStatus) liveStatus.textContent = `Playing ${word}`;
        track('synonym_pronunciation_played', { position, word, member });
      }, { once: true });
      currentAudio.addEventListener('ended', stopAudio, { once: true });
      currentAudio.addEventListener('error', () => {
        stopAudio();
        if (liveStatus) liveStatus.textContent = `Pronunciation for ${word} is temporarily unavailable.`;
      }, { once: true });
      try { await currentAudio.play(); }
      catch (_error) {
        stopAudio();
        if (liveStatus) liveStatus.textContent = `Pronunciation for ${word} could not be played.`;
      }
    };
    const makeButton = (word, reference, position) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'fba__audio button button--secondary';
      button.setAttribute('aria-label', `Listen to the pronunciation of ${word}`);
      button.setAttribute('aria-busy', 'false');
      button.title = `Listen to ${word}`;
      button.disabled = !audioEnabled || (!audioPath && !/^https:\/\//i.test(reference));
      const symbol = document.createElement('span');
      symbol.className = 'fba__audio-symbol';
      symbol.setAttribute('aria-hidden', 'true');
      symbol.textContent = '▶';
      button.append(symbol);
      if (!button.disabled) button.addEventListener('click', () => play(word, reference, button, position));
      return button;
    };
    const makeRow = (item) => {
      const row = document.createElement('li');
      row.className = 'fba__row fsy__row';
      const position = document.createElement('span');
      position.className = 'fba__position';
      position.textContent = String(item.position).padStart(4, '0');
      const words = document.createElement('strong');
      words.className = 'fba__french';
      words.lang = 'fr';
      words.textContent = `${item.french} → ${item.synonym}`;
      const sense = document.createElement('span');
      sense.className = 'fba__translation';
      sense.textContent = `Shared sense: ${item.sense}`;
      const type = document.createElement('small');
      type.className = 'fba__level';
      type.textContent = item.category;
      sense.append(type);
      const audio = document.createElement('span');
      audio.className = 'fsy__audio-group';
      audio.append(makeButton(item.french, item.headAudio, item.position), makeButton(item.synonym, item.synonymAudio, item.position));
      row.append(position, words, sense, audio);
      return row;
    };
    const render = () => {
      stopAudio();
      const filtered = query ? data.filter((item) => normalize(`${item.french} ${item.synonym} ${item.sense} ${item.category}`).includes(query)) : data;
      const pages = Math.max(1, Math.ceil(filtered.length / pageSize));
      page = Math.min(page, pages);
      const start = (page - 1) * pageSize;
      const visible = filtered.slice(start, start + pageSize);
      list.replaceChildren(...visible.map(makeRow));
      empty.hidden = filtered.length !== 0;
      count.textContent = filtered.length === data.length ? `${data.length} synonym pairs available` : `${filtered.length} matching pairs`;
      pageStatus.textContent = filtered.length ? `Showing ${start + 1}–${start + visible.length} of ${filtered.length}` : 'No results';
      previous.disabled = page <= 1;
      next.disabled = page >= pages;
    };
    search.addEventListener('input', () => {
      window.clearTimeout(debounceTimer);
      debounceTimer = window.setTimeout(() => {
        query = normalize(search.value.trim());
        page = 1;
        render();
        track('synonym_search', { has_query: Boolean(query), member });
      }, 150);
    });
    previous.addEventListener('click', () => { page -= 1; render(); root.querySelector('[data-fsy-list-heading]')?.focus(); });
    next.addEventListener('click', () => { page += 1; render(); root.querySelector('[data-fsy-list-heading]')?.focus(); });
    root.querySelector('[data-fsy-membership-cta]')?.addEventListener('click', () => track('membership_cta_clicked', { module: 'synonyms' }));
    root.querySelector('[data-fsy-sign-in]')?.addEventListener('click', () => track('sign_in_prompted', { module: 'synonyms' }));
    render();
    track('synonym_page_view', { member, available_pairs: data.length });
    if (!member) track('synonym_paywall_viewed', { locked_pairs: 900 });
  };
  document.querySelectorAll('[data-fsy-root]').forEach(initialize);
})();
